import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { range, padStart, split } from 'lodash';
import moment from 'moment';
import cx from 'classnames';
import styled, { css } from 'styled-components';
import { transparentize } from 'polished';

import SelectWrap from 'components/SelectWrap/';

const DateInputFieldsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  & .select-wrap {
    width: 100%;
  }
`;

const DateInputFieldsDayWrapper = styled.div`
  display: flex;
  flex: 2;
`;

const DateInputFieldsMonthWrapper = styled.div`
  display: flex;
  flex: 3;
  margin-left: 10px;
`;

const DateInputFieldsYearWrapper = styled.div`
  display: flex;
  flex: 2;
  margin-left: 10px;
`;

const SelectFormControl = styled.select(
  ({ theme }) => css`
    border-radius: 2px;
    font-size: 0.875rem;
    font-family: AvenirMedium, sans-serif;
    line-height: 1.25rem;
    min-height: 30px;
    display: block;
    padding: 0.5rem 0.75rem;
    background-image: none;
    background-clip: padding-box;
    border: 1px solid ${theme.colors.alto};
    transition: border-color ease-in-out 0.15s box-shadow ease-in-out 0.15s;
  `
);

export default class DateInputFields extends Component {
  static propTypes = {
    defaultValue: PropTypes.string,
    // Must be YYYY format
    fromYear: PropTypes.number.isRequired,
    toYear: PropTypes.number,
    onChange: PropTypes.func,
    className: PropTypes.string,
    themeColor: PropTypes.string,
  };

  static defaultProps = {
    defaultValue: '',
    toYear: new Date().getFullYear(), // defaults to current year
  };

  constructor(props) {
    super(props);
    this.state = {
      year: split(this.props.defaultValue, '-', 3)[0],
      month: split(this.props.defaultValue, '-', 3)[1],
      day: (split(this.props.defaultValue, '-', 3)[2] || '').substring(0, 2),
      date: this.props.defaultValue,
    };
  }

  createNumericOptions = (startValue, numberOfOptions) => {
    const arr = range(startValue, numberOfOptions);
    return arr.map(opt =>
      <option key={opt + 1} value={padStart(opt + 1, 2, '0')}>{padStart(opt + 1, 2, '0')}</option>
    );
  }

  createYearOptions = () => {
    const { toYear, fromYear } = this.props;
    return this.createNumericOptions(toYear - 1,
      fromYear - 2);
  }

  createMonthOptions = () => {
    const arr = [
      'January', 'February', 'March', 'April', 'May',
      'June', 'July', 'August', 'September',
      'October', 'November', 'December',
    ];
    return arr.map((month, i) =>
      <option key={month} value={padStart(i + 1, 2, '0')}>{month}</option>
    );
  }

  dayIsValid(day) {
    const { month, year } = this.state;
    if (!month) {
      // cant use moment#isValid() without a month
      return true;
    }
    const paddedDay = padStart(day, 2, '0');
    return moment(`${year || moment().year()}-${month}-${paddedDay}`).isValid();
  }

  handleChange = (e) => {
    const field = e.target[0].text.toLowerCase();

    this.setState({
      [field]: e.target.value,
    }, () => {
      // Need to check and validate day value is correct after udpating month/year
      let { day } = this.state;
      if (day) {
        day = this.dayIsValid(day) ? day : '';
      }
      this.setState({ day }, this.createDate);
    });
  }

  createDate() {
    const { day, month, year } = this.state;
    let date = null;

    if (day && month && year) {
      date = `${year}-${month}-${day}`;
    }

    this.setState({ date }, () => this.props.onChange(this.state.date));
  }

  renderDays() {
    const { month, year } = this.state;
    if (!month) {
      return this.createNumericOptions(31);
    }

    return this.createNumericOptions(moment(`${year || moment().year()}-${month}`, 'YYYY-MM')
      .daysInMonth());
  }

  render() {
    const { className } = this.props;
    return (
      <DateInputFieldsContainer>
        <DateInputFieldsDayWrapper>
          <SelectWrap
            value={this.state.day}
            onChange={this.handleChange}
            className={cx(className, 'date-input-fields__day')}
          >
            <SelectFormControl id="days">
              <option value="">Day</option>
              {this.renderDays()}
            </SelectFormControl>
          </SelectWrap>
        </DateInputFieldsDayWrapper>
        <DateInputFieldsMonthWrapper>
          <SelectWrap onChange={this.handleChange} className={cx(className, 'date-input-fields__month')}>
            <SelectFormControl defaultValue={this.state.month}>
              <option value="">Month</option>
              {this.createMonthOptions(12)}
            </SelectFormControl>
          </SelectWrap>
        </DateInputFieldsMonthWrapper>
        <DateInputFieldsYearWrapper>
          <SelectWrap onChange={this.handleChange} className={cx(className, 'date-input-fields__year')}>
            <SelectFormControl defaultValue={this.state.year}>
              <option value="">Year</option>
              {this.createYearOptions()}
            </SelectFormControl>
          </SelectWrap>
        </DateInputFieldsYearWrapper>
      </DateInputFieldsContainer>
    );
  }
}

/**
 * This named export is for the latest UI design
 * and is meant to be used for any new components in the platform.
 * The old style is kept in the default export just
 * to provide backwards-compatibility.
 */
export const StyledDateInputFields = styled(DateInputFields)(
  ({ theme, themeColor }) => css`
    border-radius: 4px;
    border: 1px solid ${themeColor || theme.colors.alto};
    background-color: ${themeColor && transparentize(0.95, themeColor)};
  `
);
